import React, { useState } from 'react';
import { Alert, Card, Form, Row, Col, Button, Tooltip, OverlayTrigger } from 'react-bootstrap-5';
import PropTypes from 'prop-types';
import { isEmpty, pick } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles, faInfoCircle, faComment } from '@fortawesome/pro-solid-svg-icons';
import { connect } from 'react-redux';

import AutocompletedAddressFormGroup from 'components/form/autocompleted_address_form_group';
import BrokersList from './brokers_list';
import ErrorAlert from './error_alert';
import { countryListPropType } from './prop_types';
import AiDocumentImport from './ai_document_import';
import { formatWithCommas } from 'helpers/formatters/number_formats';


const SUBMIT_ACTIONS = {
  SEARCH_OR_CREATE_LISTING: 'search_or_create_listing',
  CREATE_DOC: 'create_doc'
};

// controls submit button placement. Coupled with `properties_controller/new_listing_button_ab_segment.rb`
export const AB_SEGMENTS = {
  CREATE_DOC: 'create-doc',
  SEARCH_OR_CREATE: 'search-or-create'
};

// Fields that can be populated by AI document import
const AI_POPULATED_FIELD_NAMES = [
  'address',
  'city',
  'state',
  'zip',
  'county',
  'country_id',
  'sale_or_lease',
  'name',
  'location_description',
  'property_type_id',
  'building_size',
  'building_class',
  'sale_price',
  'broker_name',
  'lease_rate',
  'lot_size',
  'number_of_units',
  'zoning',
  'year_built',
  'year_renovated',
  'occupancy'
];

export function CreatePropertyForm({
  addressNotFound,
  aiDocUploadSuccess,
  aiPopulatedFieldsArray,
  allowUploadDoc,
  brokers,
  brokerSelectOptions,
  buttonAbSegment,
  countryList,
  currentAiFieldsArray,
  errorMessages,
  formFields,
  gotoPrevStep,
  showCreateDocButton,
  hasErrors,
  hasInsights,
  inferredFieldsArray,
  listingNotFound,
  maxBrokersLimit,
  newListing,
  propertyTypes,
  saving,
  showBrokerSelect,
  storeAiPopulatedFields,
  storeCurrentAiFields,
  storeInferredFields,
  onAiUploadStatusChange,
  onBrokerChange,
  onChange,
  onFormSubmit,
  onFormSubmitWithDocument,
}) {
  // ============================================================================
  // FORM SUBMISSION STATE
  // ============================================================================
  // We have two submit buttons with different behaviors:
  // 1. "Search/Create Listing" button - standard form submission
  // 2. "View Proposal" button - creates listing + document, redirects to editor
  //
  // - `validationTriggered`: true after ANY submit button is clicked (shows validation errors)
  // - `activeSubmitAction`: which button initiated the current save (SUBMIT_ACTIONS value | null)
  //
  // The `saving` prop from parent is true when API call is in progress (i.e. "pending").
  // We combine it with `activeSubmitAction` to show pending state on correct button.
  // ============================================================================
  const [validationTriggered, setValidationTriggered] = useState(false);
  const [activeSubmitAction, setActiveSubmitAction] = useState(null);

  // Convert Redux arrays to Sets for efficient lookup
  // These are stored in Redux so they survive remounts when user clicks Back/Next or submits the form
  const aiPopulatedFields = new Set(aiPopulatedFieldsArray);
  const currentAiFields = new Set(currentAiFieldsArray);
  const inferredFields = new Set(inferredFieldsArray);

  // NOTE: converting to camelCase will introduce complexity & fragility because we're also dealing with arrays like
  // populatedFields and inferredFields; `camelized` (backend) doesn't handle those, nor does `api.ts` convert them, so
  // we'd need to use specialized logic
  //
  /* eslint-disable camelcase */
  const {
    address,
    city,
    country,
    state,
    zip,
    county,
    sale_or_lease,
    name,
    location_description,
    property_type_id,
    building_size,
    building_class,
    generate_sale_description,
    generate_sale_bullets,
    generate_lease_description,
    generate_lease_bullets,
    sale_price,
    lease_rate,
    lot_size,
    number_of_units,
    zoning,
    year_built,
    year_renovated,
    occupancy
  } = formFields;
  /* eslint-enable camelcase */

  const isStateRequired = country.countryStates.length !== 0;
  const isZipRequired = country.zipLabel !== '';

  const isAddressInvalid = !address;
  const isCityInvalid = !city;
  const isCountryInvalid = !country?.hasOwnProperty('id');
  const isStateInvalid = isStateRequired && !state;
  const isZipInvalid = isZipRequired && !zip;
  const isPropertyTypeInvalid = !property_type_id; // eslint-disable-line camelcase
  const isBrokersInvalid = showBrokerSelect && brokers.some(item => isEmpty(item));
  const isSaleOrLeaseInvalid = !sale_or_lease; // eslint-disable-line camelcase

  // Derived state for button pending/attempted status
  const isSearchOrCreatePending = saving && activeSubmitAction === SUBMIT_ACTIONS.SEARCH_OR_CREATE_LISTING;
  const isCreateDocPending = saving && activeSubmitAction === SUBMIT_ACTIONS.CREATE_DOC;
  const createDocAttempted = activeSubmitAction === SUBMIT_ACTIONS.CREATE_DOC;

  const isFormInvalid = isAddressInvalid ||
    isCityInvalid ||
    isCountryInvalid ||
    isStateInvalid ||
    isZipInvalid ||
    isPropertyTypeInvalid ||
    isBrokersInvalid;

  const handleSearchOrCreateClick = (e) => {
    e.preventDefault();
    setValidationTriggered(true);
    setActiveSubmitAction(SUBMIT_ACTIONS.SEARCH_OR_CREATE_LISTING);
    if (!isFormInvalid) onFormSubmit();
  };

  const handleCreateDocClick = (e) => {
    if (e) e.preventDefault();
    setValidationTriggered(true);
    setActiveSubmitAction(SUBMIT_ACTIONS.CREATE_DOC);
    if (!isFormInvalid && !isSaleOrLeaseInvalid) onFormSubmitWithDocument();
  };

  // Handle form submit - routes to appropriate handler based on primary action
  const handleFormSubmit = (e) => {
    const showBothButtons = allowUploadDoc && showCreateDocButton;
    const primaryAction = showBothButtons && buttonAbSegment === AB_SEGMENTS.CREATE_DOC
      ? AB_SEGMENTS.CREATE_DOC
      : AB_SEGMENTS.SEARCH_OR_CREATE;

    if (primaryAction === AB_SEGMENTS.CREATE_DOC) {
      handleCreateDocClick(e);
    } else {
      handleSearchOrCreateClick(e);
    }
  };

  const searchOrCreateButtonText = () => {
    if (listingNotFound) {
      return isSearchOrCreatePending ? 'Creating Listing...' : 'Create Listing';
    } else if (newListing) {
      return isSearchOrCreatePending ? 'Searching...' : 'Search';
    }
    return '';
  };

  // Handle data from AI document import - mark fields as AI-populated
  const handleAiImport = (processedData) => {
    const aiFormData = { ...processedData.propertyFormData };

    // The AI returns country_id (a number), but the form state stores the full country object.
    // Look up the matching country from countryList so updateForm can dispatch change_country correctly.
    if (aiFormData.country_id !== undefined) {
      const selectedCountry = countryList.find(c => c.id === Number(aiFormData.country_id));
      if (selectedCountry) {
        aiFormData.country = selectedCountry;
      }
      delete aiFormData.country_id;
    }

    onChange(aiFormData);

    // Use populatedFieldNames from backend
    const populatedFields = processedData.populatedFieldNames || [];
    const updatedFields = new Set();

    // Use server-provided list of populated fields
    populatedFields.forEach((fieldName) => {
      if (AI_POPULATED_FIELD_NAMES.includes(fieldName)) {
        updatedFields.add(fieldName);
      }
    });

    if (updatedFields.size > 0) {
      const newAiPopulated = new Set([...aiPopulatedFields, ...updatedFields]);
      const newCurrentAi = new Set([...currentAiFields, ...updatedFields]);
      storeAiPopulatedFields(newAiPopulated);
      storeCurrentAiFields(newCurrentAi);
    }

    if (processedData.inferredFields && Array.isArray(processedData.inferredFields)) {
      storeInferredFields(new Set(processedData.inferredFields));
    }

    // Handle broker_name from AI - find matching broker and populate brokers array
    if (aiFormData.broker_name && brokerSelectOptions && brokerSelectOptions.length > 0) {
      const matchingBroker = brokerSelectOptions.find((broker) => {
        return broker.label && broker.label.toLowerCase() === aiFormData.broker_name.toLowerCase();
      });
      
      if (matchingBroker && brokers.every(b => isEmpty(b))) {
        onBrokerChange([matchingBroker]);
      }
    }

    onChange({
      aiIngestMetadata: processedData.ingestMetadata,
      // store flag in redux to survive remounts
      showAiFeedbackForm: true
    });
  };


  // Handle manual field edits - clear the sparkles icon for that field
  // `fieldName` is the name of the field in AI_POPULATED_FIELD_NAMES
  const handleManualEdit = fieldName => (data) => {
    onChange(data);

    // Hide sparkles & inferred icon for this field
    // NOTE: for simplicity, if the user manually reverts their edit, we don't bring the icon back

    if (currentAiFields.has(fieldName)) {
      const updated = new Set(currentAiFields);
      updated.delete(fieldName);
      storeCurrentAiFields(updated);
    }

    if (inferredFields.has(fieldName)) {
      const updated = new Set(inferredFields);
      updated.delete(fieldName);
      storeInferredFields(updated);
    }
  };

  // Render sparkles icon if field was AI-populated and hasn't been manually edited
  const renderSparkles = (fieldName) => {
    if (!currentAiFields.has(fieldName)) return null;

    return (
      <FontAwesomeIcon
        className="me-2"
        icon={faSparkles}
        style={{
          color: '#924FDC'
        }}
        title="AI-populated field"
      />
    );
  };

  // Render AI inferred icon if field was inferred or estimated
  const renderAiInferredIcon = (fieldName) => {
    if (!inferredFields.has(fieldName)) return null;

    const tooltip = (
      <Tooltip id={`ai-inferred-tooltip-${fieldName}`}>
        An exact match wasn&apos;t found in the document, so this value was predicted based on context.
        Please review for accuracy.
      </Tooltip>
    );

    return (
      <OverlayTrigger overlay={tooltip} placement="bottom">
        <span className="ms-2">
          <FontAwesomeIcon
            icon={faInfoCircle}
            style={{
              color: '#6c757d',
              cursor: 'help'
            }}
          />
        </span>
      </OverlayTrigger>
    );
  };

  // Helper to determine if an AI-optional field should be shown
  // These fields only appear if allowUploadDoc is enabled AND the field was populated by AI
  const shouldShowAiOptionalField = (fieldName) => {
    // Special case: sale_or_lease is always shown when allowUploadDoc is true (needed for "Create Document" button)
    if (fieldName === 'sale_or_lease' && allowUploadDoc) return true;
    return allowUploadDoc && aiPopulatedFields.has(fieldName);
  };

  // Render submit buttons with A/B test layout
  const renderSubmitButtons = () => {
    const showBothButtons = allowUploadDoc && showCreateDocButton;

    // Default to search-or-create being primary if no segment or only one button
    const primaryAction = showBothButtons && buttonAbSegment === AB_SEGMENTS.CREATE_DOC
      ? AB_SEGMENTS.CREATE_DOC
      : AB_SEGMENTS.SEARCH_OR_CREATE;

    const searchButtonVariant = primaryAction === AB_SEGMENTS.SEARCH_OR_CREATE ? 'primary' : 'outline';
    const createDocButtonVariant = primaryAction === AB_SEGMENTS.CREATE_DOC ? 'primary' : 'outline';

    const searchButton = (
      <Button
        data-ab-action={AB_SEGMENTS.SEARCH_OR_CREATE}
        data-ab-placement={primaryAction === AB_SEGMENTS.SEARCH_OR_CREATE ? 'primary' : 'secondary'}
        data-ai-doc-upload={aiDocUploadSuccess}
        data-pendo-tag="prop_form_create_listing_button"
        disabled={saving}
        name="submit-listing"
        type={primaryAction === AB_SEGMENTS.SEARCH_OR_CREATE ? 'submit' : 'button'}
        variant={searchButtonVariant}
        onClick={primaryAction === AB_SEGMENTS.SEARCH_OR_CREATE ? undefined : handleSearchOrCreateClick}
      >
        {searchOrCreateButtonText()}
      </Button>
    );

    const createDocButton = showBothButtons && (
      <Button
        data-ab-action={AB_SEGMENTS.CREATE_DOC}
        data-ab-placement={primaryAction === AB_SEGMENTS.CREATE_DOC ? 'primary' : 'secondary'}
        data-ai-doc-upload={aiDocUploadSuccess}
        data-pendo-tag="prop_form_create_proposal_button"
        disabled={saving}
        name="submit-listing-with-document"
        type={primaryAction === AB_SEGMENTS.CREATE_DOC ? 'submit' : 'button'}
        variant={createDocButtonVariant}
        onClick={handleCreateDocClick}
      >
        {isCreateDocPending ? 'Creating Proposal...' : 'View Proposal'}
      </Button>
    );

    const backButton = gotoPrevStep && (
      <Button variant="ghost" onClick={gotoPrevStep}>Back</Button>
    );

    // Layout: [secondary] ... [Back] [primary]
    // Secondary on far left, Back immediately left of primary, primary on far right
    if (showBothButtons) {
      return (
        <div className="mt-2 d-flex justify-content-between">
          <div>
            {primaryAction === AB_SEGMENTS.SEARCH_OR_CREATE ? createDocButton : searchButton}
          </div>
          <div className="d-flex">
            {backButton && <span className="me-3">{backButton}</span>}
            {primaryAction === AB_SEGMENTS.SEARCH_OR_CREATE ? searchButton : createDocButton}
          </div>
        </div>
      );
    }

    // Only one button (search/create) - original layout
    return (
      <div className="mt-2 d-flex justify-content-end">
        {backButton && <span className="me-3">{backButton}</span>}
        {searchButton}
      </div>
    );
  };

  // these fields didn't exist before AI document import feature, so omit them for now
  // NOTE: other fields are also shown/hidden based on shouldShowAiOptionalField
  const renderAiOptionalFields = () => {
    return (
      <>
        {/* NOTE: this is a special field that's only required when creating a document - see `createDocAttempted` */}
        {shouldShowAiOptionalField('sale_or_lease') && (
          <Form.Group className={`form-group ${createDocAttempted ? 'required' : ''}`}>
            <Form.Label>
              {renderSparkles('sale_or_lease')}
              Sale or Lease
              {renderAiInferredIcon('sale_or_lease')}
            </Form.Label>
            {/* eslint-disable camelcase */}
            <Form.Select
              className="form-control"
              isInvalid={isSaleOrLeaseInvalid && createDocAttempted && validationTriggered}
              name="sale_or_lease"
              value={sale_or_lease || ''}
              onChange={e => handleManualEdit('sale_or_lease')({ sale_or_lease: e.target.value })}
            >
              <option value="">Select...</option>
              <option value="sale">Sale</option>
              <option value="lease">Lease</option>
            </Form.Select>
            {isSaleOrLeaseInvalid && createDocAttempted && validationTriggered && (
              <Form.Control.Feedback type="invalid">
                Sale or Lease is required to create a document
              </Form.Control.Feedback>
            )}
            {/* eslint-enable camelcase */}
          </Form.Group>
        )}


        {/* eslint-disable-next-line camelcase */}
        {shouldShowAiOptionalField('sale_or_lease') && sale_or_lease && (
          <Form.Group className="form-group">
            <Form.Label className="d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faSparkles} style={{ color: '#924FDC' }} />
              AI Content Generation
            </Form.Label>
            {/* eslint-disable camelcase */}
            {sale_or_lease === 'sale' && (
              <>
                <Form.Check
                  checked={generate_sale_description === true}
                  id="generate-sale-description"
                  label="Auto-generate sale description"
                  type="switch"
                  onChange={e => onChange({ generate_sale_description: e.target.checked })}
                />
                <Form.Check
                  checked={generate_sale_bullets === true}
                  id="generate-sale-bullets"
                  label="Auto-generate sale highlights"
                  type="switch"
                  onChange={e => onChange({ generate_sale_bullets: e.target.checked })}
                />
              </>
            )}
            {sale_or_lease === 'lease' && (
              <>
                <Form.Check
                  checked={generate_lease_description === true}
                  id="generate-lease-description"
                  label="Auto-generate lease description"
                  type="switch"
                  onChange={e => onChange({ generate_lease_description: e.target.checked })}
                />
                <Form.Check
                  checked={generate_lease_bullets === true}
                  id="generate-lease-bullets"
                  label="Auto-generate lease highlights"
                  type="switch"
                  onChange={e => onChange({ generate_lease_bullets: e.target.checked })}
                />
              </>
            )}
            {/* eslint-enable camelcase */}
          </Form.Group>
        )}

        {shouldShowAiOptionalField('location_description') && (
          <Form.Group className="form-group">
            <Form.Label>
              {renderSparkles('location_description')}
              Location Description
              {renderAiInferredIcon('location_description')}
            </Form.Label>
            {/* eslint-disable camelcase */}
            <Form.Control
              as="textarea"
              name="location_description"
              rows={3}
              value={location_description || ''}
              onChange={e => handleManualEdit('location_description')({ location_description: e.target.value })}
            />
            {/* eslint-enable camelcase */}
          </Form.Group>
        )}

        {shouldShowAiOptionalField('building_size') && (
          <Form.Group className="form-group">
            <Form.Label>
              {renderSparkles('building_size')}
              Building Size (SF)
              {renderAiInferredIcon('building_size')}
            </Form.Label>
            {/* eslint-disable camelcase */}
            <Form.Control
              name="building_size"
              type="text"
              value={building_size || ''}
              onChange={e => handleManualEdit('building_size')({ building_size: e.target.value })}
            />
            {/* eslint-enable camelcase */}
          </Form.Group>
        )}

        {shouldShowAiOptionalField('building_class') && (
          <Form.Group className="form-group">
            <Form.Label>
              {renderSparkles('building_class')}
              Building Class
              {renderAiInferredIcon('building_class')}
            </Form.Label>
            {/* eslint-disable camelcase */}
            <Form.Select
              className="form-control"
              name="building_class"
              value={building_class || ''}
              onChange={e => handleManualEdit('building_class')({ building_class: e.target.value })}
            >
              <option value="">Select a building class</option>
              <option value="A">A</option>
              <option value="B">B</option>
              <option value="C">C</option>
            </Form.Select>
            {/* eslint-enable camelcase */}
          </Form.Group>
        )}

        {/* eslint-disable camelcase */}
        {shouldShowAiOptionalField('sale_price') && sale_or_lease === 'sale' && (
          <Form.Group className="form-group">
            <Form.Label>
              {renderSparkles('sale_price')}
              Sale Price
              {renderAiInferredIcon('sale_price')}
            </Form.Label>
            <Form.Control
              name="sale_price"
              type="text"
              value={sale_price ? formatWithCommas(sale_price) : ''}
              onChange={e => handleManualEdit('sale_price')({ sale_price: e.target.value.replace(/[^0-9.]/g, '') })}
            />
            {/* eslint-enable camelcase */}
          </Form.Group>
        )}

        {/* eslint-disable camelcase */}
        {shouldShowAiOptionalField('lease_rate') && sale_or_lease === 'lease' && (
          <Form.Group className="form-group">
            <Form.Label>
              {renderSparkles('lease_rate')}
              Lease Rate
              {renderAiInferredIcon('lease_rate')}
            </Form.Label>
            <Form.Control
              name="lease_rate"
              type="text"
              value={lease_rate ? formatWithCommas(lease_rate) : ''}
              onChange={e => handleManualEdit('lease_rate')({ lease_rate: e.target.value.replace(/[^0-9.]/g, '') })}
            />
            {/* eslint-enable camelcase */}
          </Form.Group>
        )}

        {shouldShowAiOptionalField('lot_size') && (
          <Form.Group className="form-group">
            <Form.Label>
              {renderSparkles('lot_size')}
              Lot Size (Acres)
              {renderAiInferredIcon('lot_size')}
            </Form.Label>
            {/* eslint-disable camelcase */}
            <Form.Control
              name="lot_size"
              type="number"
              value={lot_size || ''}
              onChange={e => handleManualEdit('lot_size')({ lot_size: e.target.value })}
            />
            {/* eslint-enable camelcase */}
          </Form.Group>
        )}

        {shouldShowAiOptionalField('number_of_units') && (
          <Form.Group className="form-group">
            <Form.Label>
              {renderSparkles('number_of_units')}
              Number of Units
              {renderAiInferredIcon('number_of_units')}
            </Form.Label>
            {/* eslint-disable camelcase */}
            <Form.Control
              name="number_of_units"
              type="number"
              value={number_of_units || ''}
              onChange={e => handleManualEdit('number_of_units')({ number_of_units: e.target.value })}
            />
            {/* eslint-enable camelcase */}
          </Form.Group>
        )}

        {shouldShowAiOptionalField('zoning') && (
          <Form.Group className="form-group">
            <Form.Label>
              {renderSparkles('zoning')}
              Zoning
              {renderAiInferredIcon('zoning')}
            </Form.Label>
            <Form.Control
              name="zoning"
              type="text"
              value={zoning || ''}
              onChange={e => handleManualEdit('zoning')({ zoning: e.target.value })}
            />
          </Form.Group>
        )}

        {shouldShowAiOptionalField('year_built') && (
          <Form.Group className="form-group">
            <Form.Label>
              {renderSparkles('year_built')}
              Year Built
              {renderAiInferredIcon('year_built')}
            </Form.Label>
            {/* eslint-disable camelcase */}
            <Form.Control
              name="year_built"
              type="number"
              value={year_built || ''}
              onChange={e => handleManualEdit('year_built')({ year_built: e.target.value })}
            />
            {/* eslint-enable camelcase */}
          </Form.Group>
        )}

        {shouldShowAiOptionalField('year_renovated') && (
          <Form.Group className="form-group">
            <Form.Label>
              {renderSparkles('year_renovated')}
              Year Renovated
              {renderAiInferredIcon('year_renovated')}
            </Form.Label>
            {/* eslint-disable camelcase */}
            <Form.Control
              name="year_renovated"
              type="number"
              value={year_renovated || ''}
              onChange={e => handleManualEdit('year_renovated')({ year_renovated: e.target.value })}
            />
            {/* eslint-enable camelcase */}
          </Form.Group>
        )}

        {shouldShowAiOptionalField('occupancy') && (
          <Form.Group className="form-group">
            <Form.Label>
              {renderSparkles('occupancy')}
              Occupancy
              {renderAiInferredIcon('occupancy')}
            </Form.Label>
            {/* eslint-disable camelcase */}
            <Form.Control
              name="occupancy"
              type="number"
              value={occupancy || ''}
              onChange={e => handleManualEdit('occupancy')({ occupancy: e.target.value })}
            />
            {/* eslint-enable camelcase */}
          </Form.Group>
        )}
      </>
    );
  };

  return (
    <>
      <h5 className="mb-5 mt-0 text-center">Create a new listing</h5>

      {newListing && (
        <>
          <div className="mb-5 text-center">
            Search for a property and Buildout Insights will auto-populate the property information we have,
            or you can create a brand new listing.
          </div>

          {allowUploadDoc && (
            <AiDocumentImport
              saving={saving}
              onAiUploadStatusChange={onAiUploadStatusChange}
              onProcessedData={handleAiImport}
            />
          )}

          <h6 className="my-4">Search for Property</h6>
        </>
      )}
      <ErrorAlert errorMessages={errorMessages} hasErrors={hasErrors}/>
      {listingNotFound && hasInsights && !hasErrors && (
        <>
          <Alert className="d-flex mb-5" variant="info">
            <p className="fs-large m-0">
              The address {addressNotFound} was not found in Insights. Creating new property.
            </p>
          </Alert>
        </>
      )}

      <Form autoComplete="off" className="form-vertical-field-container" noValidate onSubmit={handleFormSubmit}>
        {countryList.length > 1 && (
          <Form.Group className="form-group required">
            <Form.Label>
              {renderSparkles('country_id')}
              Country
              {renderAiInferredIcon('country_id')}
            </Form.Label>
            <Form.Select
              autoFocus={countryList.length > 1}
              className="form-control"
              isInvalid={isCountryInvalid && validationTriggered}
              name="country"
              required
              value={country.id}
              onChange={(e) => {
                const selectedCountry = countryList.find(country => country.id === Number(e.target.value));
                handleManualEdit('country')({ country: selectedCountry });
              }}
            >
              {countryList.map(country => (
                <option key={country.id} value={country.id}>{country.name}</option>
              ))}
            </Form.Select>
            {isCountryInvalid && validationTriggered && <Form.Control.Feedback type="invalid">
              This field is required
            </Form.Control.Feedback>}
          </Form.Group>
        )}

        <AutocompletedAddressFormGroup
          {...pick(formFields, ['country', 'address', 'city', 'state', 'zip'])}
          feedback={isAddressInvalid && validationTriggered && 'This field is required'}
          feedbackType="invalid"
          hasError={isAddressInvalid && validationTriggered}
          label={
            <Form.Label>
              {renderSparkles('address')}
              Address
              {renderAiInferredIcon('address')}
            </Form.Label>
          }
          required
          onChange={handleManualEdit('address')}
        />

        <Form.Group className="form-group required">
          <Form.Label>
            {renderSparkles('city')}
            City
            {renderAiInferredIcon('city')}
          </Form.Label>
          <Form.Control
            isInvalid={isCityInvalid && validationTriggered}
            name="city"
            required
            type="text"
            value={city}
            onChange={e => handleManualEdit('city')({ city: e.target.value })}
          />
          {isCityInvalid && validationTriggered && <Form.Control.Feedback type="invalid">
            This field is required
          </Form.Control.Feedback>}
        </Form.Group>

        <Form.Group>
          <Row>
            {(country.countryStates.length !== 0) && (
              <Col md={8}>
                <div className="form-group required">
                  <Form.Label>
                    {renderSparkles('state')}
                    {country.stateLabel}
                    {renderAiInferredIcon('state')}
                  </Form.Label>
                  <Form.Select
                    className="form-control"
                    isInvalid={isStateInvalid && validationTriggered}
                    name="state"
                    required
                    value={state}
                    onChange={e => handleManualEdit('state')({ state: e.target.value })}
                  >
                    <option value="">Select a state</option>
                    {country.countryStates.map(state => (
                      <option key={state.code} value={state.code}>{state.displayName}</option>
                    ))}
                  </Form.Select>
                  {isStateInvalid && validationTriggered && <Form.Control.Feedback type="invalid">
                    This field is required
                  </Form.Control.Feedback>}
                </div>
              </Col>
            )}

            {country.zipLabel && (
              <Col>
                <div className="form-group required">
                  <Form.Label>
                    {renderSparkles('zip')}
                    {country.zipLabel}
                    {renderAiInferredIcon('zip')}
                  </Form.Label>
                  <Form.Control
                    color="red"
                    isInvalid={isZipInvalid && validationTriggered}
                    name="zip"
                    required
                    type="string"
                    value={zip}
                    onChange={e => handleManualEdit('zip')({ zip: e.target.value })}
                  />
                  {isZipInvalid && validationTriggered && <Form.Control.Feedback type="invalid">
                    This field is required
                  </Form.Control.Feedback>}
                </div>
              </Col>
            )}
          </Row>
        </Form.Group>

        {shouldShowAiOptionalField('county') && country.hasCounty && (
          <Form.Group className="form-group">
            <Form.Label>
              {renderSparkles('county')}
              County
              {renderAiInferredIcon('county')}
            </Form.Label>
            <Form.Control
              name="county"
              type="text"
              value={county || ''}
              onChange={e => handleManualEdit('county')({ county: e.target.value })}
            />
          </Form.Group>
        )}

        {showBrokerSelect && <Form.Group className="form-group">
          <BrokersList
            brokers={brokers}
            isInvalid={isBrokersInvalid && validationTriggered}
            maxBrokersLimit={maxBrokersLimit}
            required={true}
            onSetBrokers={brokers => onBrokerChange(brokers)}
          />
          {isBrokersInvalid && validationTriggered && (
            <div className="text-danger small mt-1">
              This field is required
            </div>
          )}
        </Form.Group>}

        <Form.Group className="form-group required">
          <Form.Label>
            {renderSparkles('property_type_id')}
            Primary Property Type
            {renderAiInferredIcon('property_type_id')}
          </Form.Label>
          {/* eslint-disable camelcase */}
          <Form.Select
            className="form-control"
            isInvalid={isPropertyTypeInvalid && validationTriggered}
            name="property_type_id"
            required
            value={property_type_id || ''}
            onChange={e => handleManualEdit('property_type_id')({ property_type_id: e.target.value })}
          >
            <option value="">Select a property type</option>
            {propertyTypes && Object.entries(propertyTypes).map(([key, value]) => (
              <option key={key} value={key}>{value}</option>
            ))}
          </Form.Select>
          {/* eslint-enable camelcase */}
          {isPropertyTypeInvalid && validationTriggered && <Form.Control.Feedback type="invalid">
            This field is required
          </Form.Control.Feedback>}
        </Form.Group>

        {shouldShowAiOptionalField('name') && (
          <Form.Group className="form-group">
            <Form.Label>
              {renderSparkles('name')}
              Property Name
              {renderAiInferredIcon('name')}
            </Form.Label>
            <Form.Control
              name="name"
              type="text"
              value={name || ''}
              onChange={e => handleManualEdit('name')({ name: e.target.value })}
            />
          </Form.Group>
        )}

        {renderAiOptionalFields()}

        {(aiDocUploadSuccess || formFields.showAiFeedbackForm) && (
          <Card className="bg-buildout-blue-100">
            <Card.Body>
              <Form.Group className="form-group">
                <Form.Label>
                  <FontAwesomeIcon className="me-2" icon={faComment} />
                  Help us improve your data import
                </Form.Label>
                <Form.Control
                  as="textarea"
                  name="aiFeedback"
                  placeholder="How was your experience with the AI document import?"
                  rows={3}
                  value={formFields.aiFeedback || ''}
                  onChange={e => onChange({ aiFeedback: e.target.value })}
                />
              </Form.Group>
            </Card.Body>
          </Card>
        )}

        {renderSubmitButtons()}
      </Form>
    </>
  );
}

CreatePropertyForm.propTypes = {
  addressNotFound: PropTypes.string.isRequired,
  aiDocUploadSuccess: PropTypes.bool,
  aiPopulatedFieldsArray: PropTypes.array,
  allowUploadDoc: PropTypes.bool,
  brokers: PropTypes.array.isRequired,
  brokerSelectOptions: PropTypes.array,
  buttonAbSegment: PropTypes.oneOf(Object.values(AB_SEGMENTS)),
  countryList: countryListPropType.isRequired,
  currentAiFieldsArray: PropTypes.array,
  errorMessages: PropTypes.object,
  formFields: PropTypes.object.isRequired,
  gotoPrevStep: PropTypes.func,
  hasErrors: PropTypes.bool.isRequired,
  hasInsights: PropTypes.bool.isRequired,
  inferredFieldsArray: PropTypes.array,
  listingNotFound: PropTypes.bool.isRequired,
  maxBrokersLimit: PropTypes.number.isRequired,
  newListing: PropTypes.bool.isRequired,
  propertyTypes: PropTypes.objectOf(PropTypes.string),
  saving: PropTypes.bool.isRequired,
  showBrokerSelect: PropTypes.bool.isRequired,
  showCreateDocButton: PropTypes.bool,
  storeAiPopulatedFields: PropTypes.func,
  storeCurrentAiFields: PropTypes.func,
  storeInferredFields: PropTypes.func,
  onAiUploadStatusChange: PropTypes.func,
  onBrokerChange: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onFormSubmit: PropTypes.func.isRequired,
  onFormSubmitWithDocument: PropTypes.func,
};

const mapStateToProps = state => ({
  propertyTypes: state.propertyTypes,
  brokerSelectOptions: state.brokerSelectOptions || [],
  // Get AI field tracking from Redux (stored as arrays, converted to Sets in component)

  // Fields that were originally AI-populated
  aiPopulatedFieldsArray: state.newPropertyFromScratchLocation?.aiPopulatedFields || [],
  // Fields that still have AI sparkles (i.e. haven't been manually edited)
  currentAiFieldsArray: state.newPropertyFromScratchLocation?.currentAiFields || [],
  inferredFieldsArray: state.newPropertyFromScratchLocation?.inferredFields || [],
  aiDocUploadSuccess: state.newPropertyFromScratchLocation?.aiDocUploadSuccess || false
});

const mapDispatchToProps = dispatch => ({
  storeAiPopulatedFields: (fields) => {
    dispatch({ type: 'set_aiPopulatedFields', aiPopulatedFields: Array.from(fields) });
  },
  storeCurrentAiFields: (fields) => {
    dispatch({ type: 'set_currentAiFields', currentAiFields: Array.from(fields) });
  },
  storeInferredFields: (fields) => {
    dispatch({ type: 'set_inferredFields', inferredFields: Array.from(fields) });
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(CreatePropertyForm);
